import classNames from 'classnames';
import _ from 'lodash';
import { KeyboardEventHandler, useState } from 'react';
import { FieldErrors, FieldPath, FieldValues, PathValue, UseFormRegister } from 'react-hook-form';

export interface InputFormFieldProps<TFieldValues extends FieldValues> {
  register?: UseFormRegister<TFieldValues>;
  errors?: FieldErrors<TFieldValues>;
  name: FieldPath<TFieldValues>;
}

export interface InputProps<TFieldValues extends FieldValues> extends InputFormFieldProps<TFieldValues> {
  label?: string;
  type: string;
  required?: boolean;
  placeholder?: string;
  defaultValue?: PathValue<TFieldValues, FieldPath<TFieldValues>>;
  disabled?: boolean;
  readOnly?: boolean;
  className?: string;
  labelClassName?: string;
  topLabel?: boolean;
  prefix?: string;
  suffix?: string;
  hidden?: boolean;
  onKeyDown?: KeyboardEventHandler<HTMLInputElement>;
}

export type CommonInputProps<TFieldValues extends FieldValues> = Partial<InputProps<TFieldValues>>;

const Input = <TFieldValues extends FieldValues>({
  label,
  type,
  name,
  register,
  errors,
  placeholder,
  defaultValue,
  disabled = false,
  readOnly = false,
  required = false,
  className,
  labelClassName,
  topLabel = false,
  prefix,
  suffix,
  hidden = false,
  ...props
}: InputProps<TFieldValues>): JSX.Element => {
  const [showPassword, setShowPassword] = useState(false);
  const error = _.get(errors, name)?.message as string;
  const isPasswordField = type === 'password';
  const shouldShowToggle = isPasswordField && !disabled && !readOnly;
  const inputType = shouldShowToggle && showPassword ? 'text' : type;
  const inputWrapperClasses = classNames('flex w-full', { 'flex-col': topLabel, 'items-center': !topLabel });

  const labelClasses = classNames(
    'font-medium',
    {
      'block mb-1': topLabel,
      'flex-shrink-0 w-32': !topLabel
    },
    labelClassName
  );

  const customBgColor = /(bg-\S+)/.exec(className ?? '')?.[1] ?? '';
  const bgColor = {
    'bg-gray-300': disabled && !customBgColor,
    'bg-gray-200': readOnly && !disabled && !customBgColor,
    'bg-[#f6f6f6]': topLabel && !disabled && !customBgColor,
    [customBgColor]: customBgColor
  };

  const inputClasses = classNames('flex-1 min-w-0 py-2 border-none outline-none', bgColor);
  const containerClasses = classNames(
    'w-full flex pl-3 items-center rounded overflow-hidden',
    bgColor,
    className
  );

  const errorClasses = classNames('text-red-600 text-sm font-semibold mt-1', { hidden: !error || hidden });

  const EyeToggleButton = () => (
    <button
      type="button"
      className="px-2 flex-shrink-0 text-gray-600 hover:text-gray-800 focus:outline-none focus:text-gray-800 transition-colors duration-200"
      onClick={() => setShowPassword(!showPassword)}
      tabIndex={-1}
    >
      {showPassword ? (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21"
          />
        </svg>
      ) : (
        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
          />
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
          />
        </svg>
      )}
    </button>
  );

  return (
    <div className={classNames('mb-2', { hidden })}>
      <div className={inputWrapperClasses}>
        {label && (
          <label htmlFor={String(name)} className={labelClasses}>
            {label} {required && <span className="text-red-600">*</span>}
          </label>
        )}

        <div className={containerClasses}>
          {prefix && <span className="pr-2 flex-shrink-0 truncate">{prefix}</span>}
          <input
            id={String(name)}
            type={inputType}
            placeholder={placeholder}
            disabled={disabled}
            readOnly={readOnly}
            defaultValue={defaultValue}
            {...register?.(name, {
              required,
              disabled,
              setValueAs: value =>
                type === 'number' ? (value || value === '0' ? Number(value) : undefined) : value || undefined
            })}
            {...props}
            className={inputClasses}
          />
          {shouldShowToggle && <EyeToggleButton />}
          {suffix && <span className="px-2 flex-shrink-0 truncate">{suffix}</span>}
        </div>
      </div>
      {error && <p className={errorClasses}>{error}</p>}
    </div>
  );
};

export default Input;
